import React, { useState, useRef, useEffect } from 'react';
import { getProfileHeightString, getProfileWeightString, humanize, isUndefinedNullEmpty, SbCurrentUser } from '../submission_boards/component/SbModel';
import { Menu } from '@headlessui/react';
import MenuItemCustom from '../submission_boards/component/MenuItemCustom';
import {
  ArrowDownToLineIcon,
  BriefcaseBusinessIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  EllipsisVerticalIcon,
  Globe2Icon,
  GlobeIcon,
  HeartIcon,
  LayoutGridIcon,
  LocateIcon,
  LocationEditIcon,
  MailIcon,
  MapPinIcon,
  MarsIcon,
  Maximize2Icon,
  MessageSquareText,
  MoveVerticalIcon,
  PhoneIcon,
  PlusIcon,
  RulerIcon,
  SquareUserIcon,
  TextIcon,
  VideoIcon,
  WeightIcon,
  WholeWordIcon,
  PencilLine,
  CircleChevronUpIcon,
  CircleChevronDownIcon,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useIsMobile } from '../submission_boards/component/useIsMobile';
import { KitsPages } from './ProfileModal';
import SbProfileSelector from '../submission_boards/SbProfileSelector';
import imdb_logo from '../../assets/images/imdb_logo.svg'
import userService from '../services/UserService';
import { showToast } from '../shared/Toast';
import FilePreviewer from '../submission_kits/components/FilePreviewer';
import PhoneNumber from '../shared/PhoneNumber';

interface ProfileHeaderProps {
  currentUser: SbCurrentUser;
  user: any;
  projects: any;
  project: any;
  submissionBoardModel: any;
  project_user: any;
  project_folder_user: any;
  show_add_sb_button: any;
  from: any;
  selectedKit?: KitsPages;

  menuAction: (selectedKit: KitsPages) => void;
}
// component to show profile photo/age/weight/height
// used by SbDetails and SbDetailsRecipient
export const ProfileHeader: React.FC<ProfileHeaderProps> = ({
  currentUser,
  user,
  projects,
  project,
  submissionBoardModel,
  project_user,
  project_folder_user,
  show_add_sb_button,
  from,
  selectedKit,
  menuAction }) => {
  const isMobile = useIsMobile();
  const [kits, setKits] = useState(selectedKit || KitsPages.about);

  const [collapsed, setcollapsed] = useState(true);
  const [showAddToSb, setshowAddToSb] = useState(false);
  const [isContactExpanded, setIsContactExpanded] = useState<boolean>(false);
  const [isFav, setIsFav] = useState<boolean>(user?.is_fav || false);
  const [showAddTooltip, setShowAddTooltip] = useState(false);
  const [showFavTooltip, setShowFavTooltip] = useState(false);
  const [showDownloadTooltip, setShowDownloadTooltip] = useState(false);
  const [showPhoneTooltip, setShowPhoneTooltip] = useState(false);
  const [showEmailTooltip, setShowEmailTooltip] = useState(false);
  const [showImdbTooltip, setShowImdbTooltip] = useState(false);
  const [showWebsiteTooltip, setShowWebsiteTooltip] = useState(false);
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);


  // Refs for each tab to scroll active tab into view
  const [avatarUrl, setAvatarUrl] = useState<string>(user.cropped_avatar_url || user.avatar_url);

  useEffect(() => {
    const handleProfileImageUpdate = (event: CustomEvent) => {
      if (event.detail && event.detail.url) {
        setAvatarUrl(event.detail.url);
      }
    };

    window.addEventListener('profile-image-updated' as any, handleProfileImageUpdate);
    return () => {
      window.removeEventListener('profile-image-updated' as any, handleProfileImageUpdate);
    };
  }, []);

  useEffect(() => {
    // Sync with props if they change
    setAvatarUrl(user.cropped_avatar_url || user.avatar_url);
  }, [user.cropped_avatar_url, user.avatar_url]);

  const aboutTabRef = useRef<HTMLDivElement>(null);
  const workKitTabRef = useRef<HTMLDivElement>(null);
  const sKitTabRef = useRef<HTMLDivElement>(null);
  const hmuKitTabRef = useRef<HTMLDivElement>(null);
  const wardrobeKitTabRef = useRef<HTMLDivElement>(null);

  // Scroll active tab into view when kits changes or component mounts
  useEffect(() => {
    const scrollTimeout = setTimeout(() => {
      let activeTabRef: React.RefObject<HTMLDivElement> | null = null;
      switch (kits) {
        case KitsPages.about:
          activeTabRef = aboutTabRef;
          break;
        case KitsPages.workkit:
          activeTabRef = workKitTabRef;
          break;
        case KitsPages.skit:
          activeTabRef = sKitTabRef;
          break;
        case KitsPages.hairmakeupkit:
          activeTabRef = hmuKitTabRef;
          break;
        case KitsPages.wardrobekit:
          activeTabRef = wardrobeKitTabRef;
          break;
      }
      if (activeTabRef?.current) {
        activeTabRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }
    }, 100);
    return () => clearTimeout(scrollTimeout);
  }, [kits]);


  const handleMenuAction = (_kits: KitsPages) => {
    menuAction(_kits)
    setKits(_kits);
  }
  const actionMenu = () => {
    const menuOpenUpward = false;
    return (
      <>
        <div className="flex justify-end">
          <Menu as="div" className="relative inline-block text-left flex w-full">
            <Menu.Button
              id="layout_sort_id"
              className="rounded-full w-12 h-12 flex items-center justify-center overflow-hidden bg-blue-500 text-white hover:text-white hover-bg-primary-color"
            >
              <div className="w-6 h-6 flex items-center justify-center">
                <EllipsisVerticalIcon className="w-5 h-5" />
              </div>
            </Menu.Button>

            <Menu.Items
              className={`absolute flex flex-col w-56 -right-5 ${menuOpenUpward ? 'bottom-12 origin-bottom-right' : 'mt-12 origin-top-right'
                } focus:outline-none z-10`}
            >
              <div
                className="absolute right-2 text-gray-300 text-center items-center flex w-4 h-4"
                style={{
                  zIndex: 51,
                  [menuOpenUpward ? 'bottom' : 'top']: '-0.625rem',
                  right: '2.25rem',
                  width: '1rem',
                }}
              >
                <div
                  className={`absolute right-0 w-4 h-4 border-gray-300 transform bg-white ${menuOpenUpward
                    ? 'border-b-2 border-r-2 rotate-45'
                    : 'border-t-2 border-l-2 rotate-45'
                    }`}
                  style={{
                    zIndex: 51,
                    [menuOpenUpward ? 'bottom' : 'top']: '0.125rem',
                  }}
                />
              </div>

              <div className="flex flex-col w-full rounded-md py-1 px-1 gap-1 border-gray-300 border bg-white box-shadow-100 ring-1 ring-black ring-opacity-5 ">
                <MenuItemCustom
                  name={'About'}
                  icon={
                    <div className="w-4 h-4 ">
                      <SquareUserIcon className="w-4 h-4" />
                    </div>
                  }
                  selectedHandler={function (): void {
                    handleMenuAction(KitsPages.about);
                  }}
                />
                {!user.is_prod_support_onboarding && <>
                  {user?.work_kit != undefined && Object.keys(user.work_kit).length > 0 && <>
                    <hr className="w-full border-gray-100 my-1" />
                    <MenuItemCustom
                      name={'Work Kit'}
                      icon={
                        <div className="w-4 h-4 ">
                          <BriefcaseBusinessIcon className="w-4 h-4" />
                        </div>
                      }
                      selectedHandler={function (): void {
                        handleMenuAction(KitsPages.workkit);
                      }}
                    />
                  </>}
                  {user?.submission_kit_profile != undefined && Object.keys(user.submission_kit_profile).length > 0 && <>
                    <hr className="w-full border-gray-100 my-1" />
                    <MenuItemCustom
                      name={'Submission Kit'}
                      icon={
                        <div className="w-4 h-4 ">
                          <BriefcaseBusinessIcon className="w-4 h-4" />
                        </div>
                      }
                      selectedHandler={function (): void {
                        handleMenuAction(KitsPages.skit);
                      }}
                    />{' '}
                  </>}
                  {(user?.show_hair_makeup_kit ?? true) && user?.hair_make_up_kit != undefined && Object.keys(user.hair_make_up_kit).length > 0 && <>
                    <hr className="w-full border-gray-100 my-1" />
                    <MenuItemCustom
                      name={'Hair & Makeup Kit'}
                      icon={
                        <div className="w-4 h-4 ">
                          <BriefcaseBusinessIcon className="w-4 h-4" />
                        </div>
                      }
                      selectedHandler={function (): void {
                        handleMenuAction(KitsPages.hairmakeupkit);
                      }}
                    />
                  </>}

                  {(user?.show_wardrobe_kit ?? true) && user?.wardrobe_kit != undefined && Object.keys(user.wardrobe_kit).length > 0 && <>
                    <hr className="w-full border-gray-100 my-1" />
                    <MenuItemCustom
                      name={'Wardrobe Kit'}
                      icon={
                        <div className="w-4 h-4 ">
                          <BriefcaseBusinessIcon className="w-4 h-4" />
                        </div>
                      }
                      selectedHandler={function (): void {
                        handleMenuAction(KitsPages.wardrobekit);
                      }}
                    />
                  </>}
                </>}

                {currentUser?.email === user?.email && (
                  <>
                    <hr className="w-full border-gray-200 my-1" />
                    <MenuItemCustom
                      name={'Edit Profile'}
                      icon={
                        <div className="w-4 h-4 ">
                          <PencilLine className="w-4 h-4" />
                        </div>
                      }
                      selectedHandler={function (): void {
                        window.location.href = '/users/edit';
                      }}
                    />
                  </>
                )}
              </div>
            </Menu.Items>
          </Menu>
        </div>
      </>
    );
  };
  const formatPhoneNumber = (value: string) => {
    if (!value) return value;
    const phoneNumber = value.replace(/\D/g, '');
    const phoneNumberLength = phoneNumber.length;
    if (phoneNumberLength < 4) return phoneNumber;
    if (phoneNumberLength < 7) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    }
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  }
  const contactRender = () => {
    return (
      <div
        className={
          'flex flex-row flex-1 cursor-pointer ' +
          (collapsed ? ' gap-2-1 ' : ' transform transition-all duration-500 ease-in-out  opacity-100 translate-y-0 bg-white rounded-lg border-blue-502 border pt-3 pl-3 pr-1 pb-3 ')
        }
      >
        {collapsed ? (
          <>
            <div className="flex gap-2-1 items-center">
              {user?.cell_phone &&
                <div
                  className="relative"
                  onMouseEnter={() => setShowPhoneTooltip(true)}
                  onMouseLeave={() => setShowPhoneTooltip(false)}
                >
                  <a
                    href={`tel:${user?.cell_phone ? user?.cell_phone.replace(/[-\s]/g, '') : ''}`}
                    className="flex items-center justify-center w-10 h-10 rounded-full transition-colors"
                    style={{
                      backgroundColor: showPhoneTooltip ? '#0D1621' : 'transparent',
                      color: showPhoneTooltip ? '#FFFFFF' : '#374151'
                    }}
                    aria-label={`Call ${user?.cell_phone}`}
                  >
                    <PhoneIcon className="w-5 h-5" />
                  </a>
                  {showPhoneTooltip && (
                    <div className="absolute top-full left-0 mt-2 flex flex-col items-start z-50">
                      <div className="flex flex-row justify-center items-center relative text-xs font-medium leading-none text-white whitespace-nowrap" style={{ background: '#0D1621', border: '1px solid #0D1621', borderRadius: '999px', padding: '14px 16px', height: '40px', gap: '4px' }}>
                        <span style={{ color: '#BCC7DA', fontWeight: 600, fontSize: '12px', lineHeight: '100%' }}>Call:</span>
                        <span style={{ color: '#FFFFFF', fontWeight: 500, fontSize: '12px', lineHeight: '100%' }}>{formatPhoneNumber(user?.cell_phone)}</span>
                        <div className="absolute" style={{ width: '16px', height: '16px', left: '22px', top: '-7px', background: '#0D1621', borderRadius: '2px', transform: 'rotate(45deg)', zIndex: 1 }} />
                      </div>
                    </div>
                  )}
                </div>
              }
              {user?.email &&
                <div
                  className="relative"
                  onMouseEnter={() => setShowEmailTooltip(true)}
                  onMouseLeave={() => setShowEmailTooltip(false)}
                >
                  <a
                    href={`mailto:${user?.email}`}
                    className="flex items-center justify-center w-10 h-10 rounded-full transition-colors"
                    style={{
                      backgroundColor: showEmailTooltip ? '#0D1621' : 'transparent',
                      color: showEmailTooltip ? '#FFFFFF' : '#374151'
                    }}
                    aria-label={`Mail ${user?.email}`}
                  >
                    <MailIcon className="w-5 h-5" />
                  </a>
                  {showEmailTooltip && (
                    <div className="absolute top-full left-0 mt-2 flex flex-col items-start z-50">
                      <div className="flex flex-row justify-center items-center relative text-xs font-medium leading-none text-white whitespace-nowrap" style={{ background: '#0D1621', border: '1px solid #0D1621', borderRadius: '999px', padding: '14px 16px', height: '40px', gap: '4px' }}>
                        <span style={{ color: '#BCC7DA', fontWeight: 600, fontSize: '12px', lineHeight: '100%' }}>Email:</span>
                        <span style={{ color: '#FFFFFF', fontWeight: 500, fontSize: '12px', lineHeight: '100%' }}>{user?.email}</span>
                        <div className="absolute" style={{ width: '16px', height: '16px', left: '22px', top: '-7px', background: '#0D1621', borderRadius: '2px', transform: 'rotate(45deg)', zIndex: 1 }} />
                      </div>
                    </div>
                  )}
                </div>
              }
              {user?.social_kits.filter((item) => item.social_type == "imdb").length > 0 &&
                <div
                  className="relative"
                  onMouseEnter={() => setShowImdbTooltip(true)}
                  onMouseLeave={() => setShowImdbTooltip(false)}
                >
                  <a
                    href={user?.social_kits.filter((item) => item.social_type == "imdb")[0].link_url}
                    target='_blank'
                    className="flex items-center justify-center rounded-full transition-colors"
                    style={{
                      backgroundColor: showImdbTooltip ? '#0D1621' : 'transparent',
                      width: '2.5rem',
                      height: '2.5rem'
                    }}
                    aria-label="IMDB Profile"
                  >
                    <img className="h-4 object-cover" src={imdb_logo} alt="IMDB" />
                  </a>
                  {showImdbTooltip && (
                    <div className="absolute top-full left-0 mt-2 flex flex-col items-start z-50">
                      <div className="flex flex-row justify-center items-center relative text-xs font-medium leading-none text-white whitespace-nowrap" style={{ background: '#0D1621', border: '1px solid #0D1621', borderRadius: '999px', padding: '14px 16px', height: '40px', gap: '4px' }}>
                        <span style={{ color: '#BCC7DA', fontWeight: 600, fontSize: '12px', lineHeight: '100%' }}>IMDB:</span>
                        <span style={{ color: '#FFFFFF', fontWeight: 500, fontSize: '12px', lineHeight: '100%', maxWidth: '159px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.social_kits.filter((item) => item.social_type == "imdb")[0].link_url}</span>
                        <div className="absolute" style={{ width: '16px', height: '16px', left: '22px', top: '-7px', background: '#0D1621', borderRadius: '2px', transform: 'rotate(45deg)', zIndex: 1 }} />
                      </div>
                    </div>
                  )}
                </div>
              }
              {user?.social_kits.filter((item) => item.social_type == "website").length > 0 &&
                <div
                  className="relative"
                  onMouseEnter={() => setShowWebsiteTooltip(true)}
                  onMouseLeave={() => setShowWebsiteTooltip(false)}
                >
                  <a
                    href={user?.social_kits.filter((item) => item.social_type == "website")[0].link_url}
                    target='_blank'
                    className="flex items-center justify-center w-10 h-10 rounded-full transition-colors"
                    style={{
                      backgroundColor: showWebsiteTooltip ? '#0D1621' : 'transparent',
                      color: showWebsiteTooltip ? '#FFFFFF' : '#374151'
                    }}
                    aria-label="Website"
                  >
                    <GlobeIcon className="w-5 h-5" />
                  </a>
                  {showWebsiteTooltip && (
                    <div className="absolute top-full left-0 mt-2 flex flex-col items-start z-50">
                      <div className="flex flex-row justify-center items-center relative text-xs font-medium leading-none text-white whitespace-nowrap" style={{ background: '#0D1621', border: '1px solid #0D1621', borderRadius: '999px', padding: '14px 16px', height: '40px', gap: '4px' }}>
                        <span style={{ color: '#BCC7DA', fontWeight: 600, fontSize: '12px', lineHeight: '100%' }}>Website:</span>
                        <span style={{ color: '#FFFFFF', fontWeight: 500, fontSize: '12px', lineHeight: '100%', maxWidth: '159px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.social_kits.filter((item) => item.social_type == "website")[0].link_url}</span>
                        <div className="absolute" style={{ width: '16px', height: '16px', left: '22px', top: '-7px', background: '#0D1621', borderRadius: '2px', transform: 'rotate(45deg)', zIndex: 1 }} />
                      </div>
                    </div>
                  )}
                </div>
              }
            </div>
          </>
        ) : (
          <div className="flex flex-1 gap-2-1 "
            style={{
              //  transition: 'max-height 0.4s ease'
            }}>
            {/* phone */}
            <div
              className="flex flex-1 flex-col items-start gap-2 pr-2"
              style={{
                borderRight: '1px solid rgba(140, 162, 194, 0.25)',
                padding: '0.25ren 0.5rem 0.25rem 0px',
              }}
            >
              <div className="flex items-center justify-center w-9 h-9 border border-white hover-border-blue-503  rounded-full" style={{
                color: '#374151'
              }}>
                <a
                  href={`tel:${(user?.cell_phone || "").replace(/[-\s]/g, '')}`}
                  className={`flex items-center justify-center w-full ${!user?.cell_phone ? 'pointer-events-none' : ''}`}
                >
                  <PhoneIcon className={`w-5 h-5 ${!user?.cell_phone ? 'opacity-30' : ''}`} />
                </a>
              </div>
              <div className="flex flex-col items-start gap-0 pl-3 text-sm font-medium">
                <div
                  className="flex flex-col items-start font-semibold"
                  style={{
                    color: '#576C89',
                  }}
                >
                  Phone(s)
                </div>
                <div className="flex flex-col items-start">
                  {user.cell_phone ? (
                    <a
                      href={`tel:${(user?.cell_phone || "").replace(/[-\s]/g, '')}`}
                      className="flex items-center w-full"
                      aria-label={`Call ${user?.cell_phone}`}
                    >
                      <PhoneNumber
                        phone={user.cell_phone}
                        country={user.country}
                        country_code={user.country_code}
                        showIcon={false}
                      />
                    </a>
                  ) : (
                    <div className="h-4 w-28 bg-gray-200 rounded-md animate-pulse mt-1 mb-1"></div>
                  )}
                </div>
              </div>
            </div>

            {/* email */}
            <div
              className="flex flex-1 flex-col items-start gap-2 pr-2"
              style={{
                borderRight: '1px solid rgba(140, 162, 194, 0.25)',
                padding: '0.25ren 0.5rem 0.25rem 0px',
              }}
            >
              <div className="flex items-center justify-center w-9 h-9 border border-white hover-border-blue-503  rounded-full" style={{
                color: '#374151'
              }}>
                <a
                  href={`mailto:${user?.email}`}
                  className="flex items-center justify-center w-full"
                >
                  <MailIcon className="w-5 h-5" />
                </a>
              </div>
              <div className="flex flex-col items-start gap-0 pl-3 text-sm font-medium">
                <div
                  className="flex flex-col items-start font-semibold"
                  style={{
                    color: '#576C89',
                  }}
                >
                  Email
                </div>
                <a
                  href={`mailto:${user?.email}`}
                  className="flex items-center w-full"
                  style={{
                  }}
                  aria-label={`Mail ${user?.email}`}
                >
                  <div className="flex flex-col items-start">{user.email}</div>
                </a>
              </div>
            </div>

            {/* imdb */}
            <div
              className="flex flex-1 flex-col items-start gap-2 pr-2"
              style={{
                borderRight: '1px solid rgba(140, 162, 194, 0.25)',
                padding: '0.25ren 0.5rem 0.25rem 0px',
              }}
            >
              <div className="flex items-center justify-center w-14 h-9 border border-white hover-border-blue-503  rounded-full ">
                <a
                  href={user?.social_kits?.find((item) => item.social_type == "imdb")?.link_url || '#'}
                  target='_blank'
                  className={`flex items-center justify-center w-full ${!user?.social_kits?.find((item) => item.social_type == "imdb")?.link_url ? 'pointer-events-none' : ''}`}
                >

                  <img className={`ml-auto mr-auto h-4 w-8 object-cover ${!user?.social_kits?.find((item) => item.social_type == "imdb")?.link_url ? 'opacity-30 grayscale' : ''}`} src={imdb_logo} alt="" />
                </a>
              </div>
              <div className="flex flex-col items-start gap-0 pl-3 text-sm font-medium">
                <div
                  className="flex flex-col items-start font-semibold"
                  style={{
                    color: '#576C89',
                  }}
                >
                  IMDB Profile
                </div>
                {user?.social_kits?.find((item) => item.social_type == "imdb")?.link_url ? (
                  <a
                    href={user?.social_kits.find((item) => item.social_type == "imdb").link_url}
                    target='_blank'
                    className="flex items-center w-full"
                    style={{
                      maxWidth: '11rem'
                    }}
                    aria-label={`Web `}
                  >
                    <div
                      className="text-sm font-medium w-full overflow-hidden"
                      style={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        wordBreak: 'break-all',
                        lineHeight: '1.5',
                      }}
                    >
                      {user?.social_kits.find((item) => item.social_type == "imdb").link_url}
                    </div>
                  </a>
                ) : (
                  <div className="h-4 w-28 bg-gray-200 rounded-md animate-pulse mt-1 mb-1"></div>
                )}
              </div>
            </div>

            {/* website */}
            <div
              className="flex flex-1 flex-col items-start gap-2 pr-2"
              style={{
                borderRight: '1px solid rgba(140, 162, 194, 0.25)',
                padding: '0.25ren 0.5rem 0.25rem 0px',
              }}
            >
              <div className="flex items-center justify-center w-9 h-9 border border-white hover-border-blue-503  rounded-full " style={{
                color: '#374151'
              }}>
                <a
                  href={user?.social_kits?.find((item) => item.social_type == "website")?.link_url || '#'}
                  target='_blank'
                  className={`flex items-center justify-center w-full ${!user?.social_kits?.find((item) => item.social_type == "website")?.link_url ? 'pointer-events-none' : ''}`}
                >
                  <GlobeIcon className={`w-5 h-5 ${!user?.social_kits?.find((item) => item.social_type == "website")?.link_url ? 'opacity-30' : ''}`} />
                </a>
              </div>
              <div className="flex flex-col items-start gap-0 pl-3 text-sm font-medium">
                <div
                  className="flex flex-col items-start font-semibold"
                  style={{
                    color: '#576C89',
                  }}
                >
                  Website
                </div>
                {user?.social_kits?.find((item) => item.social_type == "website")?.link_url ? (
                  <a
                    href={user?.social_kits.find((item) => item.social_type == "website").link_url}
                    target='_blank'
                    className="flex items-center w-full"
                    style={{
                      maxWidth: '11rem'
                    }}
                    aria-label={`Web `}
                  >
                    <div
                      className="text-sm font-medium w-full overflow-hidden"
                      style={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        wordBreak: 'break-all',
                        lineHeight: '1.5',
                      }}
                    >
                      {user?.social_kits.find((item) => item.social_type == "website").link_url}
                    </div>
                  </a>
                ) : (
                  <div className="h-4 w-28 bg-gray-200 rounded-md animate-pulse mt-1 mb-1"></div>
                )}
              </div>
            </div>
          </div>
        )}
        <div
          className="flex items-start py-1 px-2"
          style={{ color: '#576C89' }}
          onClick={() => {
            setcollapsed(!collapsed);
          }}
        >
          <div
            className="flex items-center w-7 h-7 rounded-full justify-center text-blue-403 bg-white hover:text-white hover-bg-primary-color"
            style={{
              border: '1px solid #3C82F6'
            }}
          >
            {collapsed ? (

              <ChevronDownIcon className="w-5 h-5" style={{}} />
            ) : (
              <ChevronUpIcon className="w-5 h-5" style={{}} />
            )}
          </div>
        </div>
      </div>
    );
  };
  const contactRenderMobile = () => {
    return (
      <>
        {/* Row 2: Mobile Action Buttons - Call/Email/Text/FaceTime */}
        <div className="flex flex-row gap-2 w-full">
          <a
            href={`tel:${user?.cell_phone ? user?.cell_phone.replace(/[-\s]/g, '') : ''}`}
            className={" flex-1 " + (user.cell_phone != undefined ? 'flex' : 'hidden')}
            aria-label={`Call ${user?.cell_phone}`}
          >
            <button
              className="flex flex-col w-full items-center justify-center gap-1 px-3 py-3 rounded-lg bg-white"
              style={{ border: '1px solid #8CA2C2', color: '#2D3A4B' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F5F7FA'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
            >
              <PhoneIcon className="h-5 w-5" />
              <span className="text-xs font-medium">Call</span>
            </button>
          </a>
          <a
            href={`mailto:${user?.email}`}
            className=" flex-1 flex"
            aria-label={`Mail ${user?.email}`}
          >
            <button
              className="flex flex-col w-full items-center justify-center gap-1 px-3 py-3 rounded-lg bg-white"
              style={{ border: '1px solid #8CA2C2', color: '#2D3A4B' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F5F7FA'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
            >
              <MailIcon className="h-5 w-5" />
              <span className="text-xs font-medium">Email</span>
            </button>
          </a>
          <a
            href={`sms:${user?.cell_phone ? user?.cell_phone.replace(/[-\s]/g, '') : ''}`}
            className={" flex-1 " + (user.cell_phone != undefined ? 'flex' : 'hidden')}
            aria-label={`Call ${user?.cell_phone}`}
          >
            <button
              className="flex flex-col w-full items-center justify-center gap-1 px-3 py-3 rounded-lg bg-white"
              style={{ border: '1px solid #8CA2C2', color: '#2D3A4B' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F5F7FA'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
            >
              <MessageSquareText className="h-5 w-5" />
              <span className="text-xs font-medium">Text</span>
            </button>
          </a>
          {isIOS &&
            <a
              href={`facetime://${user?.cell_phone ? user?.cell_phone.replace(/[-\s]/g, '') : ''}`}
              className={" flex-1 " + (user.cell_phone != undefined ? 'flex' : 'hidden')}
              aria-label={`Call ${user?.cell_phone}`}
            >
              <button
                className="flex flex-col w-full items-center justify-center gap-1 px-3 py-3 rounded-lg bg-white"
                style={{ border: '1px solid #8CA2C2', color: '#2D3A4B' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F5F7FA'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
              >
                <VideoIcon className="h-5 w-5" />
                <span className="text-xs font-medium">FaceTime</span>
              </button>
            </a>}
        </div>
        {/* Contact & Links Grid */}
        <div className="rounded-lg overflow-hidden mt-0 w-full">
          {/* Mobile: Collapsible Header */}
          {isMobile && (
            <div
              className="flex flex-row justify-center items-center w-full"
              style={{
                height: '28px',
                padding: '0px',
                gap: '10px'
              }}
            >
              <div
                style={{
                  height: '0px',
                  border: '1px solid #C5D1E1',
                  flex: '1 1 0'
                }}
              />
              <button
                onClick={() => setIsContactExpanded(!isContactExpanded)}
                className="flex flex-row justify-center items-center flex-none"
                style={{
                  width: '28px',
                  height: '28px',
                  padding: '0px',
                  background: '#FFFFFF',
                  borderRadius: '999px',
                  boxSizing: 'border-box',
                  border: '1px solid #3C82F6',
                }}
              >
                <ChevronDownIcon
                  className="transition-transform duration-200"
                  style={{
                    width: '20px',
                    height: '20px',
                    color: '#576C89',
                    transform: isContactExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
                  }}
                  strokeWidth={1.5}
                />
              </button>
              <div
                style={{
                  height: '0px',
                  border: '1px solid #C5D1E1',
                  flex: '1 1 0'
                }}
              />
            </div>
          )}
          <div
            className={`transition-all duration-300 ease-in-out ${isMobile ? (isContactExpanded ? ' py-4 max-h-[1000px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden') : ''}`}
          >
            <div
              className="grid grid-cols-1 md:grid-cols-4 bg-white overflow-hidden px-4 pt-3 pb-2 border border-gray-300 rounded-lg gap-2"
            >
              {/* Phone */}
              <div className="flex flex-row sm:flex-col gap-2 pt-1 pb-3 sm:py-1 border-b sm:border-b-0">
                <div className='flex flex-row h-9 w-9'>
                  <PhoneIcon className={`h-5 w-5 ${!user?.cell_phone ? 'opacity-30' : ''}`} style={{ color: '#576C89' }} />
                </div>
                <div className='flex flex-col'>
                  <div
                    className="text-xs font-semibold tracking-wider"
                    style={{ color: '#576C89' }}
                  >
                    Phone(s)
                  </div>
                  {user?.cell_phone ? (
                    <a
                      href={`tel:${user?.cell_phone.replace(/[-\s]/g, '')}`}
                      className="flex"
                      aria-label={`Call ${user?.cell_phone}`}
                    >
                      <div
                        className="text-sm font-medium break-words whitespace-pre-line"
                        style={{ color: '#2D3A4B' }}
                      >
                        <PhoneNumber
                          phone={user.cell_phone || ''}
                          country={user.country}
                          country_code={user.country_code}
                          showIcon={false}
                        />
                      </div>
                    </a>
                  ) : (
                    <div className="h-4 w-28 bg-gray-200 rounded-md animate-pulse mt-1 mb-1"></div>
                  )}
                </div>
              </div>

              {/* Email */}
              <div
                className="flex flex-row sm:flex-col gap-2 pt-1 pb-3 sm:py-1  border-b sm:border-b-0 md:border-l sm:px-4"
                style={{ borderColor: '#D1D9E6' }}
              >
                <div className='flex flex-row h-9 w-9'>
                  <MailIcon className="h-5 w-5" style={{ color: '#576C89' }} />
                </div>
                <div className='flex flex-col'>
                  <div
                    className="text-xs font-semibold tracking-wider"
                    style={{ color: '#576C89' }}
                  >
                    Email
                  </div>
                  <a
                    href={`mailto:${user?.email}`}
                    className="flex items-center"
                    aria-label={`Mail ${user?.email}`}
                  >
                    <div
                      className="text-sm font-medium break-words"
                      style={{ color: '#2D3A4B' }}
                    >
                      {user.email}
                    </div>
                  </a>
                </div>
              </div>

              {/* IMDb */}
              {!user.is_prod_support_onboarding && <div
                className="flex flex-row sm:flex-col gap-2 pt-1 pb-3 sm:py-1  border-b sm:border-b-0 md:border-l sm:px-4"
                style={{ borderColor: '#D1D9E6' }}
              >
                <div className='flex flex-row h-9 w-9'>
                  <img className={`ml-auto mr-auto h-4 object-cover ${!user?.social_kits?.find((item) => item.social_type == "imdb")?.link_url ? 'opacity-30 grayscale' : ''}`} src={imdb_logo} alt="" />
                </div>
                <div className='flex flex-col flex-1 min-w-0'>
                  <div
                    className="text-xs font-semibold tracking-wider"
                    style={{ color: '#576C89' }}
                  >
                    IMDb Profile
                  </div>
                  {user?.social_kits?.find((item) => item.social_type == "imdb")?.link_url ? (
                    <a
                      href={user?.social_kits?.find((item) => item.social_type == "imdb").link_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium transition-colors hover:underline overflow-hidden"
                      style={{
                        color: '#2D3A4B',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        wordBreak: 'break-all',
                        lineHeight: '1.5',
                      }}
                    >
                      {user?.social_kits?.find((item) => item.social_type == "imdb")[0]?.link_url?.replace(/^https?:\/\//, '')}
                    </a>
                  ) : (
                    <div className="h-4 w-28 bg-gray-200 rounded-md animate-pulse mt-1 mb-1"></div>
                  )}
                </div>
              </div>}

              {/* Website */}
              {!user.is_prod_support_onboarding && <div
                className="flex flex-row sm:flex-col gap-2 pt-1 pb-3 sm:py-1  border-0 sm:border-b-0 md:border-l sm:px-4"
                style={{ borderColor: '#D1D9E6' }}
              >
                <div className='flex flex-row h-9 w-9'>
                  <GlobeIcon className={`h-5 w-5 ${!user?.social_kits?.find((item) => item.social_type == "website")?.link_url ? 'opacity-30' : ''}`} style={{ color: '#576C89' }} />
                </div>
                <div className='flex flex-col flex-1 min-w-0'>
                  <div
                    className="text-xs font-semibold tracking-wider"
                    style={{ color: '#576C89' }}
                  >
                    Website
                  </div>
                  {user?.social_kits?.find((item) => item.social_type == "website")?.link_url ? (
                    <a
                      href={user?.social_kits?.find((item) => item.social_type == "website").link_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium transition-colors hover:underline overflow-hidden"
                      style={{
                        color: '#2D3A4B',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        wordBreak: 'break-all',
                        lineHeight: '1.5',
                      }}
                    >
                      {user?.social_kits?.find((item) => item.social_type == "website")?.link_url?.replace(/^https?:\/\//, '')}
                    </a>
                  ) : (
                    <div className="h-4 w-28 bg-gray-200 rounded-md animate-pulse mt-1 mb-1"></div>
                  )}
                </div>
              </div>}
            </div>
          </div>
        </div>
      </>
    );
  };
  const handleAddToSubmissionBoard = () => {
    setshowAddToSb(true);
  }
  const getPdfUrl = () => {
    if ((kits == KitsPages.hairmakeupkit && user.hair_make_up_kit_pdf_path != undefined && user.hair_make_up_kit_pdf_path != "") ||
      (kits == KitsPages.wardrobekit && user.wardrobe_kit_pdf_path != undefined && user.wardrobe_kit_pdf_path != "") ||
      (kits == KitsPages.workkit && user.work_kit_pdf_path != undefined && user.work_kit_pdf_path != "")) {

      switch (kits) {
        case KitsPages.hairmakeupkit:
          return user.hair_make_up_kit_pdf_path;
          break;
        case KitsPages.wardrobekit:
          return user.wardrobe_kit_pdf_path;
          break;
        case KitsPages.workkit:
          return user.work_kit_pdf_path;
          break;

        default:
          break;
      }
    }
    return "";
  }

  const canDownloadPdf = () => {
    // If it's a public profile, everyone can download
    if (from === 'public' && currentUser?.id) return true;

    // Super admin can always download
    if (currentUser?.super_admin) return true;

    // Check if user has Pro or higher subscription tier
    const allowedTiers = ['pro', 'pro_idle', 'premium', 'enterprise'];
    const isPro = currentUser?.subscription_tier && allowedTiers.includes(currentUser.subscription_tier);
    // Allow if Pro OR if the user is an authorized coordinator in a project context (not from directory)
    return currentUser?.id && (isPro || (show_add_sb_button && from !== 'directory'));
  }

  const handleFavAction = async () => {
    try {
      const resp = await userService.toggleFavorite(user.uuid, !isFav);
      if (resp) {
        setIsFav(!isFav);
        showToast(isFav ? 'Removed from favorites.' : 'Added to favorites.', 'success');
      }
    } catch (error) {
      showToast('Failed to update favorite.', 'danger');
    }
  }

  // Count visible options before action menu
  // const hasPdfButton = (kits == KitsPages.hairmakeupkit && user.hair_make_up_kit_pdf_path != undefined && user.hair_make_up_kit_pdf_path != "") ||
  //   (kits == KitsPages.wardrobekit && user.wardrobe_kit_pdf_path != undefined && user.wardrobe_kit_pdf_path != "") ||
  //   (kits == KitsPages.workkit && user.work_kit_pdf_path != undefined && user.work_kit_pdf_path != "");

  // const hasFavButton = user.id != currentUser.id;
  // const hasAddToSbButton = user.id != currentUser.id && show_add_sb_button;

  // const visibleOptionsCount = (hasFavButton ? 1 : 0) + (hasAddToSbButton ? 1 : 0);
  const showPipeBorder = true; // visibleOptionsCount > 0;

  const getName = () => {
    if (user?.first_name == null || user?.last_name == null) {
      return '';
    }
    let name = (user.first_name?.charAt(0)?.toUpperCase() + user.first_name?.slice(1)?.toLowerCase()) + ' ' + (user.last_name?.charAt(0)?.toUpperCase() + user.last_name?.slice(1)?.toLowerCase());
    if (name.length > 25) {
      name = (user.first_name?.charAt(0)?.toUpperCase() + user.first_name?.slice(1)?.toLowerCase()) + ' ' + (user.last_name?.charAt(0)?.toUpperCase());
      if (name.length > 25) {
        name = (user.first_name?.charAt(0)?.toUpperCase() + user.first_name?.slice(1)?.toLowerCase()).slice(0, 25);
      }
    }
    return name;
  }

  const renderMenuDesktop = () => {
    let selected = '  text-black border-blue-503 bg-blue-503-15 ';
    let notSelected = ' bg-white primary-color border-gray-300 hover:bg-gray-50 ';
    let about = notSelected;
    let workkit = notSelected;
    let skit = notSelected;
    let hairmakeupkit = notSelected;
    let wardrobekit = notSelected;
    switch (kits) {
      case KitsPages.about:
        about = selected;
        break;
      case KitsPages.workkit:
        workkit = selected;
        break;
      case KitsPages.skit:
        skit = selected;
        break;
      case KitsPages.hairmakeupkit:
        hairmakeupkit = selected;
        break;
      case KitsPages.wardrobekit:
        wardrobekit = selected;
        break;

      default:
        about = selected;
        break;
    }

    return <div className="flex items-start justify-between w-full pt-2">
      <nav className="hidden sm:flex -mb-px overflow-x-auto w-full" aria-label="Tabs"
        style={{
          "-webkit-overflow-scrolling": "touch"
        }}
        data-controller="profile-nav-scroll" data-action="turbo:load-&gt;profile-nav-scroll#scrollToActive">
        <div className="flex px-0 py-2 gap-1-1">
          {!user.is_prod_support_onboarding && <div className={"whitespace-nowrap px-4 py-2 rounded-lg font-medium text-sm border cursor-pointer" + about}
            onClick={() => {
              handleMenuAction(KitsPages.about);
            }}>About</div>}

          {!user.is_prod_support_onboarding && <>
            {user?.work_kit != undefined && Object.keys(user.work_kit).length > 0 && <div className={"whitespace-nowrap px-4 py-2 rounded-lg font-medium text-sm border cursor-pointer" + workkit}
              onClick={() => {
                handleMenuAction(KitsPages.workkit);
              }}>Work Kit</div>}
            {(user?.show_hair_makeup_kit ?? true) && user?.hair_make_up_kit != undefined && Object.keys(user.hair_make_up_kit).length > 0 && <div className={"whitespace-nowrap px-4 py-2 rounded-lg font-medium text-sm border cursor-pointer" + hairmakeupkit}
              onClick={() => {
                handleMenuAction(KitsPages.hairmakeupkit);
              }}>HMU Kit</div>}
            {(user?.show_wardrobe_kit ?? true) && user?.wardrobe_kit != undefined && Object.keys(user.wardrobe_kit).length > 0 && <div className={"whitespace-nowrap px-4 py-2 rounded-lg font-medium text-sm border cursor-pointer" + wardrobekit}
              onClick={() => {
                handleMenuAction(KitsPages.wardrobekit);
              }}>Wardrobe Kit</div>}
            {user?.submission_kit_profile != undefined && Object.keys(user.submission_kit_profile).length > 0 && <div className={"whitespace-nowrap px-4 py-2 rounded-lg font-medium text-sm border cursor-pointer" + skit}
              onClick={() => {
                handleMenuAction(KitsPages.skit);
              }}>Submission Kit</div>}
          </>}
        </div>
      </nav>
    </div>;

  }
  const renderMenuMobile = () => {
    let selected = '  text-black border-blue-503 bg-blue-503-15 ';
    let notSelected = ' bg-white primary-color border-gray-300 hover:bg-gray-50 ';
    let about = notSelected;
    let workkit = notSelected;
    let skit = notSelected;
    let hairmakeupkit = notSelected;
    let wardrobekit = notSelected;
    switch (kits) {
      case KitsPages.about:
        about = selected;
        break;
      case KitsPages.workkit:
        workkit = selected;
        break;
      case KitsPages.skit:
        skit = selected;
        break;
      case KitsPages.hairmakeupkit:
        hairmakeupkit = selected;
        break;
      case KitsPages.wardrobekit:
        wardrobekit = selected;
        break;

      default:
        about = selected;
        break;
    }

    return <div className="border-b border-gray-200 flex items-start justify-between w-full py-5">
      <nav className="sm:hidden flex -mb-px overflow-x-auto w-full" aria-label="Tabs"
        style={{
          "-webkit-overflow-scrolling": "touch"
        }}
        data-controller="profile-nav-scroll" data-action="turbo:load-&gt;profile-nav-scroll#scrollToActive">
        <div className="flex gap-1-1">
          {!user.is_prod_support_onboarding && <div ref={aboutTabRef} className={"whitespace-nowrap px-4 py-2 rounded-lg font-medium text-sm border cursor-pointer" + about}
            onClick={() => {
              handleMenuAction(KitsPages.about);
            }}>About</div>}
          {!user.is_prod_support_onboarding && <>
            {user?.work_kit != undefined && Object.keys(user.work_kit).length > 0 && <div ref={workKitTabRef} className={"whitespace-nowrap px-4 py-2 rounded-lg font-medium text-sm border cursor-pointer" + workkit}
              onClick={() => {
                handleMenuAction(KitsPages.workkit);
              }}>Work Kit</div>}
            {(user?.show_hair_makeup_kit ?? true) && user?.hair_make_up_kit != undefined && Object.keys(user.hair_make_up_kit).length > 0 && <div ref={hmuKitTabRef} className={"whitespace-nowrap px-4 py-2 rounded-lg font-medium text-sm border cursor-pointer" + hairmakeupkit}
              onClick={() => {
                handleMenuAction(KitsPages.hairmakeupkit);
              }}>HMU Kit</div>}
            {(user?.show_wardrobe_kit ?? true) && user?.wardrobe_kit != undefined && Object.keys(user.wardrobe_kit).length > 0 && <div ref={wardrobeKitTabRef} className={"whitespace-nowrap px-4 py-2 rounded-lg font-medium text-sm border cursor-pointer" + wardrobekit}
              onClick={() => {
                handleMenuAction(KitsPages.wardrobekit);
              }}>Wardrobe Kit</div>}
            {user?.submission_kit_profile != undefined && Object.keys(user.submission_kit_profile).length > 0 && <div ref={sKitTabRef} className={"whitespace-nowrap px-4 py-2 rounded-lg font-medium text-sm border cursor-pointer" + skit}
              onClick={() => {
                handleMenuAction(KitsPages.skit);
              }}>Submission Kit</div>}
          </>}
        </div>
      </nav>
    </div>;
  }
  return (
    <>
      {!isMobile && <div className={"flex items-start gap-5" +
        (collapsed ? "" : " transform transition-all duration-1000 ease-in-out opacity-100 translate-y-0")
      }>
        <div className={"profile-header-image-container flex items-start rounded-lg overflow-hidden"}
          style={{
            width: collapsed ? '20%' : '26.5%', // headerHeightChanged, 
            // height: '10.5rem',
            backgroundColor: '#fefeffff',
          }}>
          {<>
            {collapsed && (<FilePreviewer
              file_url={(avatarUrl)?.startsWith('icons/') ? `/assets/${avatarUrl}` : (avatarUrl)}
              file_name={'Profile photo'}
              file_content_type={'image/jpeg'}
              showControl={true}
              dynamicHeight={true}
              object_fit={(user.cropped_avatar_url || user.avatar_url)?.startsWith('icons/') ? 'object-contain' : 'object-cover'}
            />)}
            {!collapsed && <FilePreviewer
              file_url={(avatarUrl)?.startsWith('icons/') ? `/assets/${avatarUrl}` : (avatarUrl)}
              file_name={'Profile photo'}
              file_content_type={'image/jpeg'}
              showControl={true}
              dynamicHeight={true}
              object_fit={(user.cropped_avatar_url || user.avatar_url)?.startsWith('icons/') ? 'object-contain' : 'object-cover'}
            />}
          </>}
        </div>
        {/* <img
          src={(user.cropped_avatar_url || user.avatar_url)?.startsWith('icons/') ? `/assets/${user.cropped_avatar_url || user.avatar_url}` : (user.cropped_avatar_url || user.avatar_url)}
          alt="Profile photo"
          className={`rounded-lg ${(user.cropped_avatar_url || user.avatar_url)?.startsWith('icons/') ? 'object-contain' : 'object-cover'}`}
          style={{
            width: collapsed ? '9.5625rem' : '15.28rem',
            height: collapsed ? '12.75rem' : '18.4rem',
            backgroundColor: '#f6f7f9ff',
          }}
        /> */}
        <div className="flex flex-col flex-1 gap-2" >
          {/* primary */}
          <div className="flex flex-col flex-1">
            <div className={"flex flex-col flex-1 gap-1" +
              (collapsed ? ' pb-1 ' : ' pb-1 ')
            }>
              <div className="flex flex-col sm:flex-row flex-1 justify-between items-center">
                {/* name start */}
                <div className="flex flex-row items-center gap-2">
                  <div
                    className="font-semibold p-0 m-0 flex items-center"
                    style={{
                      fontSize: isMobile ? '1.25rem' : '2.5rem',
                      lineHeight: '120%'
                    }}
                  >
                    {getName()}

                  </div>
                  {user.pro_or_higher && (
                    <div
                      className="bg-black text-white text-xs font-semibold px-2 py-1 rounded-full flex flex-row justify-center items-center gap-1 h-7"
                      style={{
                        border: '1px solid #6B7280',
                      }}
                    >
                      PRO
                    </div>
                  )}
                </div>
                {/* name end */}
                {/* icons */}
                {!user.is_prod_support_onboarding && <div className="flex flex-row gap-1-1">
                  {currentUser?.id && ((user.currentuser_is_fav_allowed && currentUser.id != user.id && user.id > 0) || from !== 'public' || currentUser.super_admin) && <div
                    className="relative"
                    onMouseEnter={() => setShowFavTooltip(true)}
                    onMouseLeave={() => setShowFavTooltip(false)}
                  >
                    <div
                      className={"cursor-pointer rounded-full w-12 h-12 flex items-center justify-center overflow-hidden bg-white text-blue-403 " +
                        ((user.currentuser_is_fav_allowed || currentUser.super_admin) && currentUser.id != user.id && user.id > 0 ? '  hover:text-white hover-bg-primary-color ' : 'bg-white opacity-30 border border-blue-502')
                      }
                      onClick={() => {
                        if ((user.currentuser_is_fav_allowed || currentUser.super_admin) && currentUser.id != user.id && user.id > 0) {
                          handleFavAction();
                        }
                      }}
                    >
                      <HeartIcon className="w-5 h-5" style={{ fill: isFav ? '#8F79F1' : 'transparent', color: isFav ? '#8F79F1' : 'currentColor' }} />
                    </div>
                    {showFavTooltip && (
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 flex flex-col items-center z-50">
                        <div className="flex flex-row justify-center items-center relative text-xs font-medium leading-none text-white whitespace-nowrap" style={{ background: '#0D1621', border: '1px solid #0D1621', borderRadius: '999px', padding: '0.875rem 1rem', height: '2.5rem' }}>
                          {isFav ? 'Remove Favorite' : 'Add Favorite'}
                          <div className="absolute" style={{ width: '1rem', height: '1rem', left: 'calc(50% - 1rem/2)', top: '-0.4375rem', background: '#0D1621', borderRadius: '2px', transform: 'rotate(45deg)', zIndex: 1 }} />
                        </div>
                      </div>
                    )}
                  </div>}


                  {currentUser?.id && ((user.id > 0 && (show_add_sb_button || currentUser.super_admin)) || from !== 'public') && //user.id != currentUser.id && show_add_sb_button &&
                    <div
                      className="relative"
                      onMouseEnter={() => setShowAddTooltip(true)}
                      onMouseLeave={() => setShowAddTooltip(false)}
                    >
                      <div
                        className={"cursor-pointer rounded-full w-12 h-12 flex items-center justify-center overflow-hidden bg-white border-blue-502 border-r text-blue-403" +
                          (user.id > 0 && (show_add_sb_button || currentUser.super_admin) ? '  hover:text-white hover-bg-primary-color ' : 'bg-white opacity-30 border border-blue-502 ')
                        }
                        onClick={() => {
                          if (user.id > 0 && (show_add_sb_button || currentUser.super_admin)) {
                            handleAddToSubmissionBoard();
                          }
                        }}
                      >
                        <PlusIcon className="w-5 h-5" />
                      </div>
                      {showAddTooltip && (
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 flex flex-col items-center z-50">
                          <div className="flex flex-row justify-center items-center relative text-xs font-medium leading-none text-white whitespace-nowrap" style={{ background: '#0D1621', border: '1px solid #0D1621', borderRadius: '999px', padding: '0.875rem 1rem', height: '2.5rem' }}>
                            Add to Submission Board
                            <div className="absolute" style={{ width: '1rem', height: '1rem', left: 'calc(50% - 1rem/2)', top: '-0.4375rem', background: '#0D1621', borderRadius: '2px', transform: 'rotate(45deg)', zIndex: 1 }} />
                          </div>
                        </div>
                      )}
                    </div>
                  }
                  {showPipeBorder && (((user.currentuser_is_fav_allowed && currentUser && currentUser.id != user.id && user.id > 0) || (user.id > 0 && (show_add_sb_button || (currentUser && currentUser.super_admin)))) && (canDownloadPdf()) || from !== 'public') && (
                    <div
                      className="flex items-center justify-center overflow-hidden  border-r"
                      style={{
                        borderColor: '#8CA2C280',
                        marginLeft: '0.625rem',
                        marginRight: '0.625rem',
                        width: '0.125rem'
                      }}
                    >
                    </div>
                  )}
                  {kits !== KitsPages.skit && kits !== KitsPages.about && (canDownloadPdf() || from !== 'public') &&
                    <div
                      className="relative"
                      onMouseEnter={() => setShowDownloadTooltip(true)}
                      onMouseLeave={() => setShowDownloadTooltip(false)}
                    >
                      <a className={"cursor-pointer rounded-full w-12 h-12 flex items-center justify-center overflow-hidden bg-white border-blue-502 border-r text-blue-403 " +
                        (canDownloadPdf() && ((kits == KitsPages.hairmakeupkit && user.hair_make_up_kit_pdf_path != undefined && user.hair_make_up_kit_pdf_path != "") ||
                          (kits == KitsPages.wardrobekit && user.wardrobe_kit_pdf_path != undefined && user.wardrobe_kit_pdf_path != "") ||
                          (kits == KitsPages.workkit && user.work_kit_pdf_path != undefined && user.work_kit_pdf_path != ""))
                          ? ' cursor-pointer hover:text-white hover-bg-primary-color ' : ' cursor-not-allowed pointer-events-none bg-white opacity-30 border border-blue-502 ')
                      }
                        href={canDownloadPdf() ? getPdfUrl() : '#'}
                        target='_blank'
                      >
                        <div
                          className={"cursor-pointer rounded-full w-12 h-12 flex items-center justify-center overflow-hidden bg-white border-blue-502 border-r text-blue-403" +
                            (user.id > 0 ? '  hover:text-white hover-bg-primary-color ' : 'bg-white opacity-30 border border-blue-502 ')
                          }
                        >
                          <ArrowDownToLineIcon className="w-5 h-5" />
                        </div>
                      </a>
                      {showDownloadTooltip && (
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 flex flex-col items-center z-50">
                          <div className="flex flex-row justify-center items-center relative text-xs font-medium leading-none text-white whitespace-nowrap" style={{ background: '#0D1621', border: '1px solid #0D1621', borderRadius: '999px', padding: '0.875rem 1rem', height: '2.5rem' }}>
                            {canDownloadPdf() ? 'Download PDF' : 'Upgrade to Pro for PDF downloads'}
                            <div className="absolute" style={{ width: '1rem', height: '1rem', left: 'calc(50% - 1rem/2)', top: '-0.4375rem', background: '#0D1621', borderRadius: '2px', transform: 'rotate(45deg)', zIndex: 1 }} />
                          </div>
                        </div>
                      )}
                    </div>}

                  {actionMenu()}
                </div>}
              </div>
              {/* syca_check show only if project or project_folder is present */}

              <span className="text-xl text-gray-600  font-semibold hidden md:block capitalize"
                style={{
                  minHeight: '1.5rem'
                }}>
                {humanize(project_folder_user?.role_name || project_user?.role_name)}
              </span>

              {/* <p className="text-xl text-gray-600  font-semibold">Performer</p> */}
            </div>
            <div className="text-lg primary-color flex flex-row gap-1 mt-0">
              <div className="text-blue-404 pt-1">
                <MapPinIcon className="w-4 h-4" />
              </div>
              <div className="text-lg primary-color flex flex-col sm:flex-row sm:items-center">
                <span>{[user.city, user.state].filter(Boolean).join(', ')}</span>
                {user.secondary_location && [user.secondary_location.city, user.secondary_location.state].filter(Boolean).length > 0 && (
                  <span className="hidden sm:inline mx-1">• </span>
                )}
                <span>{user.secondary_location && [user.secondary_location.city, user.secondary_location.state].filter(Boolean).join(', ')}</span>
              </div>
            </div>
            {!user.is_prod_support_onboarding && <div className="text-lg primary-color  flex flex-row gap-1 mt-0">
              <div className="text-blue-404 pt-1">
                <RulerIcon className="w-4 h-4" />
              </div>
              <div className="text-lg primary-color">
                {humanize(user.gender)}{' • '}{getProfileHeightString(user, currentUser)}
                {isUndefinedNullEmpty(getProfileHeightString(user, currentUser)) ||
                  isUndefinedNullEmpty(getProfileWeightString(user, currentUser))
                  ? ''
                  : ' • '}{' '}
                {getProfileWeightString(user, currentUser)}
              </div>
            </div>}
            {renderMenuDesktop()}
          </div>
          {/* contact */}
          {contactRender()}
        </div>
        {showAddToSb && <div className="flex justify-center md:justify-end">
          <SbProfileSelector
            hideButton={true}
            currentUser={currentUser}
            user={user}
            from='sbprofile'
            projects={projects}
            project={project}
            submissionBoardModel={submissionBoardModel}
            alreadyInSb={false}
            dialogCloseHandler={function (open: boolean): void {
              setshowAddToSb(false);
            }} />
          {/* <%= react_component("SbProfileSelector", 
                          props: { 
                          currentUser: @current_user,
                          from:'sbprofile',
                          projects: @projects,
                          project: nil,
                          submissionBoardModel: nil,
                          user: @user,
                          alreadyInSb: false,
                          }, prerender: false) %> */}
        </div>}
      </div>
      }
      {isMobile &&
        <> <>{renderMenuMobile()}</>
          <div className={"flex flex-col items-start gap-8 px-2 "}>
            {/* icons */}
            <div className="flex flex-row justify-center items-center w-full" style={{ height: '1.75rem', padding: '0px', gap: '0.625rem' }}>
              {/* Left line */}
              <div style={{ height: '0px', border: '1px solid #C5D1E1', flex: '1 1 0' }} />

              {/* Icons in the middle */}
              <div className="flex flex-row gap-1-1">
                {currentUser?.id && ((user.currentuser_is_fav_allowed && currentUser.id != user.id && user.id > 0) || from !== 'public' || currentUser.super_admin) && <div
                  className="relative"
                  onMouseEnter={() => setShowFavTooltip(true)}
                  onMouseLeave={() => setShowFavTooltip(false)}
                >
                  <div
                    className={"cursor-pointer rounded-full w-12 h-12 flex items-center justify-center overflow-hidden bg-white text-blue-403 " +
                      ((user.currentuser_is_fav_allowed || currentUser.super_admin) && currentUser.id != user.id && user.id > 0 ? '  hover:text-white hover-bg-primary-color ' : 'bg-white opacity-30 border border-blue-502 ')
                    }
                    onClick={() => {
                      if ((user.currentuser_is_fav_allowed || currentUser.super_admin) && currentUser.id != user.id && user.id > 0) {
                        handleFavAction();
                      }
                    }}
                  >
                    <HeartIcon className="w-5 h-5" style={{ fill: isFav ? '#8F79F1' : 'transparent', color: isFav ? '#8F79F1' : 'currentColor' }} />
                  </div>
                  {showFavTooltip && (
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 flex flex-col items-center z-50">
                      <div className="flex flex-row justify-center items-center relative text-xs font-medium leading-none text-white whitespace-nowrap" style={{ background: '#0D1621', border: '1px solid #0D1621', borderRadius: '999px', padding: '0.875rem 1rem', height: '2.5rem' }}>
                        {isFav ? 'Remove Favorite' : 'Add Favorite'}
                        <div className="absolute" style={{ width: '1rem', height: '1rem', left: 'calc(50% - 1rem/2)', top: '-0.4375rem', background: '#0D1621', borderRadius: '2px', transform: 'rotate(45deg)', zIndex: 1 }} />
                      </div>
                    </div>
                  )}
                </div>}


                {currentUser?.id && ((user.id > 0 && (show_add_sb_button || currentUser.super_admin)) || from !== 'public') &&
                  <div
                    className="relative"
                    onMouseEnter={() => setShowAddTooltip(true)}
                    onMouseLeave={() => setShowAddTooltip(false)}
                  >
                    <div
                      className={"cursor-pointer rounded-full w-12 h-12 flex items-center justify-center overflow-hidden bg-white border-blue-502 border-r text-blue-403" +
                        (user.id > 0 && (show_add_sb_button || currentUser.super_admin) ? '  hover:text-white hover-bg-primary-color ' : 'bg-white opacity-30 border border-blue-502 ')
                      }
                      onClick={() => {
                        if (user.id > 0 && (show_add_sb_button || currentUser.super_admin)) {
                          handleAddToSubmissionBoard();
                        }
                      }}
                    >
                      <PlusIcon className="w-5 h-5" />
                    </div>
                    {showAddTooltip && (
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 flex flex-col items-center z-50">
                        <div className="flex flex-row justify-center items-center relative text-xs font-medium leading-none text-white whitespace-nowrap" style={{ background: '#0D1621', border: '1px solid #0D1621', borderRadius: '999px', padding: '0.875rem 1rem', height: '2.5rem' }}>
                          Add to Submission Board
                          <div className="absolute" style={{ width: '1rem', height: '1rem', left: 'calc(50% - 1rem/2)', top: '-0.4375rem', background: '#0D1621', borderRadius: '2px', transform: 'rotate(45deg)', zIndex: 1 }} />
                        </div>
                      </div>
                    )}
                  </div>
                }
                {showPipeBorder && (((user.currentuser_is_fav_allowed && currentUser && currentUser.id != user.id && user.id > 0) || (user.id > 0 && (show_add_sb_button || (currentUser && currentUser.super_admin)))) && (canDownloadPdf()) || from !== 'public') && (
                  <div
                    className="flex items-center justify-center overflow-hidden  border-r"
                    style={{
                      borderColor: '#8CA2C280',
                      marginLeft: '0.625rem',
                      marginRight: '0.625rem',
                      width: '0.125rem'
                    }}
                  >
                  </div>
                )}
                  {kits !== KitsPages.skit && kits !== KitsPages.about && (canDownloadPdf() || from !== 'public') &&
                    <div
                      className="relative"
                      onMouseEnter={() => setShowDownloadTooltip(true)}
                      onMouseLeave={() => setShowDownloadTooltip(false)}
                    >
                      <a className={"rounded-full w-12 h-12 flex items-center justify-center overflow-hidden bg-white border-blue-502 border-r text-blue-403" +
                        (kits !== KitsPages.about && canDownloadPdf() && ((kits == KitsPages.hairmakeupkit && user.hair_make_up_kit_pdf_path != undefined && user.hair_make_up_kit_pdf_path != "") ||
                          (kits == KitsPages.wardrobekit && user.wardrobe_kit_pdf_path != undefined && user.wardrobe_kit_pdf_path != "") ||
                          (kits == KitsPages.workkit && user.work_kit_pdf_path != undefined && user.work_kit_pdf_path != ""))
                          ? ' cursor-pointer hover:text-white hover-bg-primary-color ' : ' cursor-not-allowed pointer-events-none bg-white opacity-30 border border-blue-502 ')
                      }

                        href={canDownloadPdf() ? getPdfUrl() : '#'}
                        target='_blank'
                      >
                        <div
                          className={"cursor-pointer rounded-full w-12 h-12 flex items-center justify-center overflow-hidden bg-white border-blue-502 border-r text-blue-403" +
                            (user.id > 0 ? '  hover:text-white hover-bg-primary-color ' : 'bg-white opacity-30 border border-blue-502 ')
                          }
                        >
                          <ArrowDownToLineIcon className="w-5 h-5" />
                        </div>
                      </a>
                      {showDownloadTooltip && (
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 flex flex-col items-center z-50">
                          <div className="flex flex-row justify-center items-center relative text-xs font-medium leading-none text-white whitespace-nowrap" style={{ background: '#0D1621', border: '1px solid #0D1621', borderRadius: '999px', padding: '0.875rem 1rem', height: '2.5rem' }}>
                            {canDownloadPdf() ? 'Download PDF' : 'Upgrade to Pro for PDF downloads'}
                            <div className="absolute" style={{ width: '1rem', height: '1rem', left: 'calc(50% - 1rem/2)', top: '-0.4375rem', background: '#0D1621', borderRadius: '2px', transform: 'rotate(45deg)', zIndex: 1 }} />
                          </div>
                        </div>
                      )}
                    </div>}
                {actionMenu()}
              </div>

              {/* Right line */}
              <div style={{ height: '0px', border: '1px solid #C5D1E1', flex: '1 1 0' }} />
            </div>
            <div className={"flex flex-col items-start gap-4 w-full"}>
              <div className={"flex items-start gap-4 w-full"}>
                <div className={"profile-header-image-container flex items-start rounded-lg overflow-hidden"}
                  style={{
                    width: '34%',
                    // height: '10.5rem',
                    backgroundColor: '#fefeffff',
                  }}>
                  <FilePreviewer file_url={(avatarUrl)?.startsWith('icons/') ? `/assets/${avatarUrl}` : (avatarUrl)}
                    file_name={'Profile photo'}
                    file_content_type={'image/jpeg'}
                    showControl={true}
                    dynamicHeight={true}
                    object_fit={(user.cropped_avatar_url || user.avatar_url)?.startsWith('icons/') ? 'object-contain' : 'object-cover'}
                  />
                </div>
                {/* <img
                src={(user.cropped_avatar_url || user.avatar_url)?.startsWith('icons/') ? `/assets/${user.cropped_avatar_url || user.avatar_url}` : (user.cropped_avatar_url || user.avatar_url)}
                alt="Profile photo"
                className={`rounded-lg ${(user.cropped_avatar_url || user.avatar_url)?.startsWith('icons/') ? 'object-contain' : 'object-cover'}`}
                style={{
                  width: '34%',
                  height: '10.5rem',
                  backgroundColor: '#fefeffff',
                }}
              /> */}
                <div className="flex flex-col flex-1 gap-4">
                  {/* primary */}
                  <div className="flex flex-col flex-1 gap-2-1">
                    <div className={"flex flex-col flex-1 gap-1" +
                      (collapsed ? ' ' : ' pb-5 ')
                    }>
                      <div className="flex flex-col sm:flex-row flex-1 justify-start items-start">
                        {/* name start */}
                        <div className="flex flex-col items-start gap-2">
                          {user.pro_or_higher && (
                            <div
                              className="bg-black text-white text-xs font-semibold px-2 py-1 rounded-full flex flex-row justify-center items-center gap-1 h-4"
                              style={{
                                border: '1px solid #6B7280',
                              }}
                            >
                              PRO
                            </div>
                          )}
                          <div
                            className="font-semibold p-0 m-0"
                            style={{
                              fontSize: isMobile ? '1.25rem' : '2.5rem',
                              lineHeight: '120%'
                            }}
                          >
                            {getName()}
                          </div>
                        </div>
                        {/* name end */}
                      </div>
                      {/* syca_check show only if project or project_folder is present */}

                      <span className="text-xl text-gray-600  font-semibold hidden md:block capitalize"
                        style={{
                          minHeight: '1.25rem'
                        }}>
                        {humanize(project_folder_user?.role || project_user?.role)}
                      </span>
                    </div>
                    <div className="text-lg primary-color flex flex-col">
                      <div className="text-lg primary-color flex flex-row gap-1">
                        <div className="hidden sm:flex text-blue-404">
                          <MapPinIcon className="w-4 h-4" />
                        </div>
                        <div className="text-sm font-normal primary-color flex flex-col sm:flex-row sm:items-center">
                          <span>{[user.city, user.state].filter(Boolean).join(', ')}</span>
                          {user.secondary_location && [user.secondary_location.city, user.secondary_location.state].filter(Boolean).length > 0 && (
                            <span className="hidden sm:inline mx-1">• </span>
                          )}
                          <span>{user.secondary_location && [user.secondary_location.city, user.secondary_location.state].filter(Boolean).join(', ')}</span>
                        </div>
                      </div>
                      <div className="text-lg primary-color  flex flex-row gap-1">
                        <div className="hidden sm:flex text-blue-404">
                          <RulerIcon className="w-4 h-4" />
                        </div>
                        <div className="text-sm font-normal primary-color">
                          {humanize(user.gender)}{' • '}{getProfileHeightString(user, currentUser)}
                          {isUndefinedNullEmpty(getProfileHeightString(user, currentUser)) ||
                            isUndefinedNullEmpty(getProfileWeightString(user, currentUser))
                            ? ''
                            : ' • '}{' '}
                          {getProfileWeightString(user, currentUser)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* contact */}
              {contactRenderMobile()}
            </div>

            {showAddToSb && <div className="flex justify-center md:justify-end">
              <SbProfileSelector
                hideButton={true}
                currentUser={currentUser}
                user={user}
                from='sbprofile'
                projects={projects}
                project={project}
                submissionBoardModel={submissionBoardModel}
                alreadyInSb={false}
                dialogCloseHandler={function (open: boolean): void {
                  setshowAddToSb(false);
                }} />
              {/* <%= react_component("SbProfileSelector", 
                              props: { 
                              currentUser: @current_user,
                              from:'sbprofile',
                              projects: @projects,
                              project: nil,
                              submissionBoardModel: nil,
                              user: @user,
                              alreadyInSb: false,
                              }, prerender: false) %> */}
            </div>}
          </div>
        </>
      }
    </>
  );
};
